import React, { useLayoutEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useApiData } from '@/hooks/useApiData';
import { buildPageMeta, normalizeContext, SITE } from '@/seo/meta.mjs';

// Sets the same per-route tags that scripts/prerender.js writes into the static HTML.
function tagsFor(meta) {
  const url = meta.canonical || `${SITE}${meta.path}`;
  const image = meta.ogImage;
  return [
    <meta key="description" name="description" content={meta.description} />,
    <meta key="robots" name="robots" content={meta.robots} />,
    meta.canonical && <link key="canonical" rel="canonical" href={meta.canonical} />,
    ...meta.alternates.map(a => <link key={`alt-${a.hreflang}`} rel="alternate" hreflang={a.hreflang} href={a.href} />),
    <meta key="og:type" property="og:type" content={meta.ogType} />,
    <meta key="og:title" property="og:title" content={meta.title} />,
    <meta key="og:description" property="og:description" content={meta.description} />,
    <meta key="og:url" property="og:url" content={url} />,
    <meta key="og:locale" property="og:locale" content={meta.lang === 'hi' ? 'hi_IN' : 'en_IN'} />,
    <meta key="og:image" property="og:image" content={image.url} />,
    <meta key="og:image:width" property="og:image:width" content={String(image.width)} />,
    <meta key="og:image:height" property="og:image:height" content={String(image.height)} />,
    <meta key="og:image:alt" property="og:image:alt" content={image.alt} />,
    meta.article && <meta key="article:published" property="article:published_time" content={meta.article.publishedTime} />,
    meta.article && <meta key="article:modified" property="article:modified_time" content={meta.article.modifiedTime} />,
    <meta key="twitter:card" name="twitter:card" content="summary_large_image" />,
    <meta key="twitter:title" name="twitter:title" content={meta.title} />,
    <meta key="twitter:description" name="twitter:description" content={meta.description} />,
    <meta key="twitter:image" name="twitter:image" content={image.url} />,
    <meta key="twitter:image:alt" name="twitter:image:alt" content={image.alt} />,
    ...meta.schemas.map((schema, i) => <script key={`ld-${i}`} type="application/ld+json">{JSON.stringify(schema)}</script>),
  ].filter(Boolean);
}

export default function RouteSEO() {
  const { pathname } = useLocation();
  const { data } = useApiData('/api/seo/context', null);
  const ctx = useMemo(() => data && normalizeContext(data), [data]);
  const meta = useMemo(() => ctx && buildPageMeta(pathname, ctx), [ctx, pathname]);

  // React 19 hoists these tags itself; drop the prerendered copies (data-rh) before paint so no duplicates show.
  useLayoutEffect(() => {
    if (meta) document.querySelectorAll('head [data-rh="true"]').forEach(el => el.remove());
  }, [meta]);

  if (!meta) return null;
  return (
    <Helmet htmlAttributes={{ lang: meta.lang }}>
      <title>{meta.title}</title>
      {tagsFor(meta)}
    </Helmet>
  );
}
